import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Section } from '@/components/shared/layout/section';
import { formatPrice } from '@/components/shared/utility/format';
import { styled } from '@/stitches.config';
import type { PartsBatteryTableProps } from '@/types/shared/parts';

const TableWrap = styled(Box, {
  maxWidth: '100%',
  overflowX: 'auto',
  scrollbarWidth: 'none', // For Firefox
  '-ms-overflow-style': 'none', // For Internet Explorer and Edge Legacy
  '&::-webkit-scrollbar': {
    display: 'none', // For all modern browsers (Chrome, Safari, Edge)
  },
  '& table': {
    tableLayout: 'auto',
    borderCollapse: 'collapse',
    borderSpacing: 0,
    width: '100%',
    paddingBottom: '$space-px',
    '& th, & td': {
      textAlign: 'center',
      verticalAlign: 'middle',
      paddingTop: 14,
      paddingBottom: '$space-3',
      px: '$space-4',
      minWidth: '$size-40',
      span: {
        wordBreak: 'break-word',
        whiteSpace: 'nowrap',
      },
    },
    '& thead': {
      '& th, & td': {
        backgroundColor: '$primary100',
        borderRight: '1px solid $white',
        '&:last-child': { borderRight: 0 },
      },
      '& tr:not(:first-child)': {
        '& th, & td': { borderTop: '1px solid $white' },
      },
    },
    '& tbody': {
      '& th, & td': {
        width: `${(1 / 7) * 100}%`,
        borderRight: '1px solid $gray400',
        borderBottom: '1px solid $gray400',
        '&:last-child': { borderRight: 0 },
      },
      '& tr:nth-child(even)': {
        backgroundColor: '$gray300',
      },
      '& tr:nth-child(odd)': {
        backgroundColor: '$white',
      },
    },
  },
});

export interface BatteryTableProps extends PartsBatteryTableProps {
  dataTest?: string;
  dataTestSection?: string;
}

export const BatteryTable = ({
  batteryTable,
  dataTest = 'partDetail',
  dataTestSection = 'section_battery_table',
}: BatteryTableProps) => {
  const formatModelYear = (val: string | undefined) => (val ? val : '-');

  if (!batteryTable || !batteryTable.items?.length) return null;

  const hasDetailedData = batteryTable.items.some(
    (o) => o.dmaxYear || o.muxYear || o.mu7Year
  );

  return (
    <Section test={dataTestSection}>
      <Container size="desktop-only">
        <TableWrap
          marginTop={{
            '@initial': '8',
            '@sm': '10',
            '@md': '14',
          }}
          test={{
            dataTest: `${dataTest}_battery_full_table`,
          }}
        >
          <table>
            <thead>
              {hasDetailedData ? (
                <>
                  <tr>
                    <th rowSpan={2}>
                      <Text variant="text-white" line-height="125" font="bold">
                        กำลังไฟ <br />
                        (แอมป์)
                      </Text>
                    </th>
                    <th rowSpan={2}>
                      <Text variant="text-white" line-height="125" font="bold">
                        รุ่น
                      </Text>
                    </th>
                    <th colSpan={3}>
                      <Text variant="text-white" line-height="125" font="bold">
                        รุ่นรถ
                      </Text>
                    </th>
                    <th colSpan={2}>
                      <Text variant="text-white" line-height="125" font="bold">
                        ราคา
                      </Text>
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <Text variant="text-white" line-height="125" font="bold">
                        ดีแมคซ์
                      </Text>
                    </th>
                    <th>
                      <Text variant="text-white" line-height="125" font="bold">
                        มิว-เอ็กซ์
                      </Text>
                    </th>
                    <th>
                      <Text variant="text-white" line-height="125" font="bold">
                        มิว-เซเว่น
                      </Text>
                    </th>
                    <th>
                      <Text variant="text-white" line-height="125" font="bold">
                        ปกติ
                      </Text>
                    </th>
                    <th>
                      <Text variant="text-white" line-height="125" font="bold">
                        สมาชิก <br />
                        “อีซูซุ ยิ่งเข้า ยิ่งคุ้ม”
                      </Text>
                    </th>
                  </tr>
                </>
              ) : (
                <tr>
                  <th>
                    <Text variant="text-white" line-height="125" font="bold">
                      รุ่น
                    </Text>
                  </th>
                  <th>
                    <Text variant="text-white" line-height="125" font="bold">
                      ราคาปกติ
                    </Text>
                  </th>
                  <th>
                    <Text
                      variant="text-white"
                      line-height="125"
                      font="bold"
                      wrap
                      dangerouslySetInnerHTML={{
                        __html: `ราคาพิเศษ<br /> สมาชิก <span>“อีซูซุเอลฟ์ ยิ่งเข้า ยิ่งคุ้ม”</span>`,
                      }}
                    />
                  </th>
                </tr>
              )}
            </thead>
            <tbody>
              {batteryTable.items.map((o, i) => {
                const years = [o.dmaxYear, o.muxYear, o.mu7Year];
                const uniqueYears = Array.from(new Set(years.filter(Boolean)));
                const yearCounts = years.reduce((acc, year) => {
                  if (year) {
                    acc[year] = (acc[year] || 0) + 1;
                  }
                  return acc;
                }, {});

                const hasDuplicates =
                  uniqueYears.length !== years.filter(Boolean).length;

                return hasDetailedData ? (
                  <tr key={i} data-test={`${dataTest}_battery_table_row_${i}`}>
                    <td>
                      <Text variant="text-gray-darkest" size="text-base-2">
                        {o.ampere || '-'}
                      </Text>
                    </td>
                    <td>
                      <Text variant="text-gray-darkest" size="text-base-2">
                        {o.model || '-'}
                      </Text>
                    </td>
                    {hasDuplicates
                      ? uniqueYears.map((year, idx) => {
                          const colSpan = yearCounts[year];
                          return (
                            <td key={idx} colSpan={colSpan}>
                              <Text
                                variant="text-gray-darkest"
                                size="text-base-2"
                              >
                                {formatModelYear(year)}
                              </Text>
                            </td>
                          );
                        })
                      : years.map((year, idx) => (
                          <td key={idx}>
                            <Text
                              variant="text-gray-darkest"
                              size="text-base-2"
                            >
                              {formatModelYear(year)}
                            </Text>
                          </td>
                        ))}
                    {['price', 'specialPrice'].map((priceKey) => (
                      <td key={priceKey}>
                        <Text variant="text-gray-darkest" size="text-base-2">
                          {o[priceKey] !== null
                            ? formatPrice(o[priceKey], false)
                            : '-'}
                        </Text>
                      </td>
                    ))}
                  </tr>
                ) : (
                  <tr key={i} data-test={`${dataTest}_battery_table_row_${i}`}>
                    <td>
                      <Text variant="text-gray-darkest" size="text-base-2">
                        {o.model || '-'}
                      </Text>
                    </td>
                    <td>
                      <Text variant="text-gray-darkest" size="text-base-2">
                        {o.price !== null ? formatPrice(o.price, false) : '-'}
                      </Text>
                    </td>
                    <td>
                      <Text variant="text-red" size="text-base-2" font="bold">
                        {o.specialPrice !== null
                          ? formatPrice(o.specialPrice, false)
                          : '-'}
                      </Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      </Container>
    </Section>
  );
};

BatteryTable.displayName = 'BatteryTable';
